import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import { CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { setLayoutFooter } from '../../../utils/layout';
import { actions as cartActions } from '../../../store/ducks/cart.duck';
import { actions as profileActions } from '../../../store/ducks/profile.duck';
import useCrudSnackbar from '../../../hooks/useCrudSnackbar';
import { IStore } from '../../../interfaces/store';
import { AuthDialog } from '../../auth/AuthDialog';
import { useDefineUserRole, useFormatMessage } from '../../../hooks';
import { useCartStyles } from './hooks/useCartStyles';
import { useFormikCart } from './hooks/useFormikCart';
import { useDownloadCartXlsx } from './hooks/useDownloadCartXlsx';
import { CartForm } from './components/CartForm';
import { CartItems } from './components/CartItems';
import {
  selectByCoordsAddress,
  selectByCoordsSuccess,
  selectCart,
  selectCheckoutError,
  selectCheckoutSuccess,
  selectGuestCart,
  selectIsAuthorized,
  selectMe,
} from './selectors';
import { CompanyForm } from './components/CompanyForm';

export function Cart() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const classes = useCartStyles();
  const navigate = useNavigate();
  const fm = useFormatMessage();
  const me = useSelector(selectMe);
  const cart = useSelector(selectCart);
  const guestCart = useSelector(selectGuestCart);
  const checkoutSuccess = useSelector(selectCheckoutSuccess);
  const checkoutError = useSelector(selectCheckoutError);
  const isAuthorized = useSelector(selectIsAuthorized);
  const byCoordsAddress = useSelector(selectByCoordsAddress);
  const byCoordsSuccess = useSelector(selectByCoordsSuccess);

  const isRoleBuyer = useDefineUserRole(me, ['ROLE_BUYER', 'ROLE_BUYER_STAFF']);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const { download: downloadXlsx, downloadGuest: downloadGuestXlsx, loading: xlsxLoading } =
    useDownloadCartXlsx();
  const [pickupLocation, setPickupLocation] = useState<IStore | null>(null);

  const localLocation = useMemo(() => {
    const localLocation = localStorage.getItem('location');
    const parseLocalLocation = localLocation && JSON.parse(localLocation);
    return parseLocalLocation;
  }, []);

  if (isAuthorized && me && !isRoleBuyer) {
    navigate('/');
  }

  const formik = useFormikCart({
    setAuthDialogOpen,
    localLocation,
    pickupLocation,
  });

  const { values, setFieldValue } = formik;

  setLayoutFooter({ show: true });

  useCrudSnackbar({
    success: checkoutSuccess,
    error: checkoutError,
    clear: () => dispatch(cartActions.clearCheckout()),
    successMessage: intl.formatMessage({ id: 'CART.CHECKOUT.SUCCESS' }),
    errorMessage: `${intl.formatMessage({ id: 'ERROR' })}: ${checkoutError}`,
    afterSuccessOrError: () => navigate('/orders/list/full'),
  });

  useEffect(() => {
    dispatch(profileActions.fetchRequest());
  }, []);

  useEffect(() => {
    if (me && me.location) {
      setFieldValue('location', me.location);
    }
  }, [me]);
  //
  useEffect(() => {
    if (byCoordsSuccess) {
      if (byCoordsAddress) {
        setFieldValue('location', byCoordsAddress);
      }
    }
  }, [byCoordsSuccess]);

  let cartEmpty = true;

  if (isAuthorized) {
    cartEmpty = !cart?.items.length;
  } else {
    cartEmpty = !guestCart?.items.length;
  }

  const canDownloadXlsx = isAuthorized
    ? Boolean(cart?.cart_hash)
    : Boolean(guestCart?.items.length);

  const handleDownloadXlsx = () => {
    if (isAuthorized && cart?.cart_hash) {
      downloadXlsx(cart.cart_hash);
      return;
    }

    if (guestCart?.items.length) {
      downloadGuestXlsx(guestCart);
    }
  };

  return (
    <>
      <Row className={classes.row}>
        <div className={classes.header}>
          <p className={classes.headerTitle}>{fm('CART.HEADER.LIST')}</p>
          {canDownloadXlsx && (
            <Tooltip title={fm('CART.DOWNLOAD.XLSX')}>
              <span>
                <IconButton
                  disabled={xlsxLoading}
                  onClick={handleDownloadXlsx}
                  size='small'
                  style={{ marginLeft: 8 }}
                  className={classes.xlsxBtn}
                >
                  {xlsxLoading ? (
                    <CircularProgress size={20} color='inherit' />
                  ) : (
                    <img src='/media/icons/xls.svg' alt='xls' />
                  )}
                  <span className={classes.xlsxText}>{fm('CART.DOWNLOAD.XLSX')}</span>
                </IconButton>
              </span>
            </Tooltip>
          )}
        </div>
        <Col className={classes.col}>
          {!cartEmpty && (cart || guestCart) ? (
            <div className={classes.container}>
              <div className={classes.containerItems}>
                <CartItems />
              </div>
              <div className={classes.rigthCol}>
                <CompanyForm />
                <CartForm
                  formik={formik}
                  pickupLocation={pickupLocation}
                  setPickupLocation={setPickupLocation}
                />
              </div>
            </div>
          ) : (
            <Typography className={classes.empty} component='h5' variant='h5'>
              {intl.formatMessage({ id: 'COMMON.LIST.EMPTY' })}
            </Typography>
          )}
        </Col>
      </Row>
      <AuthDialog
        initPhone={values.phone}
        initEmail={values.email}
        hideGuestBtn={Boolean(true)}
        isOpen={authDialogOpen}
        handleClose={() => setAuthDialogOpen(false)}
      />
    </>
  );
}
